import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  getPayoutMethodDetail,
  reviewPayoutMethod,
  PayoutMethodDetailResponse,
  PayoutMethodVerificationStatus,
} from '../services/cashoutApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const verificationBadge = (status: string) => {
  if (status === 'PENDING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const wdBadge = (status: string) => {
  if (status === 'processing') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'failed') return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const DetailRow: React.FC<{ label: string; value?: string | null; mono?: boolean }> = ({ label, value, mono }) => (
  <div className='flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0'>
    <span className='text-sm text-gray-500 shrink-0'>{label}</span>
    <span className={`text-sm font-medium text-gray-900 text-right break-all ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
  </div>
);

const BankAccountDetailPage: React.FC = () => {
  const { methodId } = useParams<{ methodId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PayoutMethodDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const load = useCallback(async () => {
    if (!methodId) return;
    setLoading(true);
    try {
      const result = await getPayoutMethodDetail(methodId);
      setData(result);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load bank account');
    } finally {
      setLoading(false);
    }
  }, [methodId]);

  useEffect(() => { load(); }, [load]);

  const handleVerification = async (status: PayoutMethodVerificationStatus, note?: string) => {
    if (!methodId) return;
    if (status === 'REJECTED' && !note?.trim()) { toast.error('Rejection reason is required'); return; }
    setActionLoading(status);
    try {
      await reviewPayoutMethod(methodId, status, note);
      toast.success(status === 'PENDING' ? 'Reset to pending' : `Bank account ${status.toLowerCase()}`);
      setShowRejectForm(false);
      setRejectNote('');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-16 text-center'>
          <p className='text-gray-500'>Bank account not found.</p>
          <button onClick={() => navigate('/cashout')} className='mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'>
            ← Back to Cashout
          </button>
        </div>
      </div>
    );
  }

  const { method, user_profile, withdrawal_requests } = data;
  const isApproved = method.verification_status === 'APPROVED';
  const isRejected = method.verification_status === 'REJECTED';
  const isPending = method.verification_status === 'PENDING';

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />
      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <button onClick={() => navigate('/cashout')} className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900'>
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' /></svg>
          Back to Cashout
        </button>

        {/* Header */}
        <div className='mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <p className='text-xs text-gray-400 font-mono mb-1'>{method.method_type?.toUpperCase()} · {method.method_id}</p>
              <h1 className='text-xl font-bold text-gray-900'>{method.display_name || method.bank_name || 'Bank Account'}</h1>
              {method.account_holder_name && <p className='text-sm text-gray-500'>{method.account_holder_name}</p>}
            </div>
            <div className='flex flex-col items-end gap-2'>
              <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${verificationBadge(method.verification_status)}`}>
                {method.verification_status}
              </span>
              {method.is_default && (
                <span className='inline-flex rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700'>Default</span>
              )}
            </div>
          </div>
          <div className='mt-4 flex flex-wrap gap-4 border-t border-gray-100 pt-4 text-sm text-gray-600'>
            <span><span className='font-semibold text-gray-800'>Added:</span> {formatDate(method.created_at)}</span>
            {method.verified_at && <span><span className='font-semibold text-gray-800'>Verified:</span> {formatDate(method.verified_at)}</span>}
            {method.verification_note && <span className='text-rose-600'><span className='font-semibold'>Note:</span> {method.verification_note}</span>}
          </div>
        </div>

        <div className='grid gap-6 lg:grid-cols-3'>
          {/* Left: bank details */}
          <div className='lg:col-span-2 space-y-6'>
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <h2 className='mb-4 text-base font-semibold text-gray-900'>Bank Details</h2>
              {method.method_type === 'bank' && (
                <>
                  <DetailRow label='Bank Name' value={method.bank_name} />
                  <DetailRow label='Account Holder' value={method.account_holder_name} />
                  {method.account_number_last4 && <DetailRow label='Account No.' value={`****${method.account_number_last4}`} mono />}
                  {method.iban && <DetailRow label='IBAN' value={method.iban} mono />}
                  {method.swift_bic && <DetailRow label='SWIFT/BIC' value={method.swift_bic} mono />}
                  {method.ifsc_code && <DetailRow label='IFSC Code' value={method.ifsc_code} mono />}
                  {method.routing_number && <DetailRow label='Routing No.' value={method.routing_number} mono />}
                  {method.sort_code && <DetailRow label='Sort Code' value={method.sort_code} mono />}
                  {method.bsb_code && <DetailRow label='BSB Code' value={method.bsb_code} mono />}
                  {method.transit_number && <DetailRow label='Transit No.' value={method.transit_number} mono />}
                  {method.institution_number && <DetailRow label='Institution No.' value={method.institution_number} mono />}
                  {method.local_routing_code && <DetailRow label='Local Routing' value={method.local_routing_code} mono />}
                  {method.currency_code && <DetailRow label='Currency' value={method.currency_code} />}
                  {method.bank_street_address && <DetailRow label='Bank Address' value={`${method.bank_street_address}${method.bank_city ? ', ' + method.bank_city : ''}${method.bank_postcode ? ' ' + method.bank_postcode : ''}${method.bank_country ? ', ' + method.bank_country : ''}`} />}
                  {method.receiver_street_address && <DetailRow label='Receiver Address' value={`${method.receiver_street_address}${method.receiver_city ? ', ' + method.receiver_city : ''}${method.receiver_postcode ? ' ' + method.receiver_postcode : ''}${method.receiver_country ? ', ' + method.receiver_country : ''}`} />}
                </>
              )}
              {method.method_type === 'upi' && <DetailRow label='UPI ID' value={method.upi_id} mono />}
              {method.method_type === 'paypal' && <DetailRow label='PayPal Email' value={method.paypal_email} />}
              {method.method_type === 'card' && (
                <>
                  <DetailRow label='Card' value={`${method.card_brand || 'Card'} ****${method.card_last4}`} />
                  <DetailRow label='Expiry' value={method.card_expiry_month && method.card_expiry_year ? `${method.card_expiry_month}/${method.card_expiry_year}` : null} />
                  <DetailRow label='Billing Country' value={method.billing_country} />
                </>
              )}
            </div>

            {/* Withdrawal requests using this method */}
            {withdrawal_requests.length > 0 && (
              <div className='rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
                <div className='border-b border-gray-200 px-5 py-4'>
                  <h2 className='text-base font-semibold text-gray-900'>Withdrawal Requests</h2>
                  <p className='text-sm text-gray-500'>Requests made using this payout method.</p>
                </div>
                <div className='overflow-x-auto'>
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                      <tr>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reference</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Tokens</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Net USD</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Created</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100 bg-white'>
                      {withdrawal_requests.map((wd) => (
                        <tr
                          key={wd.request_id}
                          onClick={() => navigate(`/cashout/withdrawals/${encodeURIComponent(wd.request_id)}`)}
                          className='cursor-pointer hover:bg-orange-50/50'
                        >
                          <td className='px-5 py-3 text-sm font-mono text-gray-700'>{wd.reference_id}</td>
                          <td className='px-5 py-3 text-sm text-gray-700'>{wd.tokens.toLocaleString()}</td>
                          <td className='px-5 py-3 text-sm font-semibold text-gray-900'>${wd.net_usd.toFixed(2)}</td>
                          <td className='px-5 py-3 text-sm'>
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${wdBadge(wd.status)}`}>{wd.status}</span>
                          </td>
                          <td className='px-5 py-3 text-sm text-gray-500'>{formatDate(wd.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Right: user + actions */}
          <div className='space-y-4'>
            {/* Actions */}
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <h2 className='mb-4 text-base font-semibold text-gray-900'>Verification</h2>
              <div className='space-y-3'>
                {!isApproved && (
                  <button
                    onClick={() => handleVerification('APPROVED')}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    {actionLoading === 'APPROVED' ? 'Approving…' : isRejected ? 'Re-Approve' : 'Approve'}
                  </button>
                )}
                {!isRejected && (
                  <button
                    onClick={() => { setShowRejectForm((v) => !v); setRejectNote(''); }}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    {isApproved ? 'Re-Reject' : 'Reject'}
                  </button>
                )}
                {showRejectForm && (
                  <div className='space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3'>
                    <textarea
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      rows={2}
                      placeholder='Rejection reason (required)'
                      className='w-full rounded-lg border border-rose-300 px-3 py-2 text-sm focus:border-rose-500 focus:outline-none'
                    />
                    <button
                      onClick={() => handleVerification('REJECTED', rejectNote)}
                      disabled={Boolean(actionLoading)}
                      className='w-full rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60'
                    >
                      {actionLoading === 'REJECTED' ? 'Submitting…' : 'Confirm Reject'}
                    </button>
                  </div>
                )}
                {!isPending && (
                  <button
                    onClick={() => handleVerification('PENDING')}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    {actionLoading === 'PENDING' ? 'Resetting…' : 'Reset to Pending'}
                  </button>
                )}
                <button onClick={load} className='w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50'>
                  Refresh
                </button>
              </div>
            </div>

            {/* User */}
            {user_profile && (
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <h2 className='mb-3 text-base font-semibold text-gray-900'>Account Owner</h2>
                {user_profile.profile_image_url ? (
                  <img src={user_profile.profile_image_url} alt='' className='h-12 w-12 rounded-full object-cover mb-3' />
                ) : (
                  <div className='flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] text-lg font-bold text-white mb-3'>
                    {(user_profile.user_name || user_profile.user_id).charAt(0).toUpperCase()}
                  </div>
                )}
                <DetailRow label='Username' value={user_profile.user_name ? `@${user_profile.user_name}` : user_profile.user_id} />
                <DetailRow label='Account Status' value={user_profile.account_status} />
                <button
                  onClick={() => navigate(`/cashout/${encodeURIComponent(user_profile.user_id)}`)}
                  className='mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50'
                >
                  View Cashout Application →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankAccountDetailPage;
